import { useEffect, useRef, useState } from "react";
import { Minus, Paperclip, Plus, X } from "lucide-react";
import { api, money } from "@/lib/api";
import { AccountSelect } from "@/components/AccountSelect";

const today = () => new Date().toISOString().slice(0, 10);
const emptyLine = () => ({ account_code: "", description: "", debit: "", credit: "" });

export default function JournalForm({ onCancel, onSaved, initial }) {
  const fileRef = useRef(null);
  const [accounts, setAccounts] = useState([]);
  const [head, setHead] = useState(initial ? { date: initial.date, tag: initial.tag || "", memo: initial.memo || initial.description || "" } : { date: today(), tag: "", memo: "" });
  const [lines, setLines] = useState(initial ? initial.lines.map((l) => ({ account_code: l.account_code, description: l.description || "", debit: l.debit || "", credit: l.credit || "" })) : [emptyLine(), emptyLine()]);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { api("/accounts").then(setAccounts).catch(() => {}); }, []);
  const setLine = (i, k, v) => setLines(lines.map((l, x) => {
    if (x !== i) return l;
    const next = { ...l, [k]: v };
    if (k === "debit" && v) next.credit = "";
    if (k === "credit" && v) next.debit = "";
    return next;
  }));
  const filled = lines.filter((l) => l.account_code && (Number(l.debit) > 0 || Number(l.credit) > 0));
  const totalDebit = filled.reduce((s, l) => s + (Number(l.debit) || 0), 0);
  const totalCredit = filled.reduce((s, l) => s + (Number(l.credit) || 0), 0);
  const balanced = totalDebit > 0 && Math.round(totalDebit * 100) === Math.round(totalCredit * 100);
  const valid = filled.length >= 2 && balanced && head.date;
  const pickFile = (f) => {
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { setError("Ukuran lampiran maksimal 10 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setFile({ filename: f.name, content_type: f.type || "application/octet-stream", data: String(reader.result).split(",")[1] });
    reader.readAsDataURL(f);
  };
  const submit = async () => {
    setSaving(true); setError("");
    try {
      const payload = {
        date: head.date, tag: head.tag, memo: head.memo, description: head.memo || `Jurnal umum ${head.date}`,
        lines: filled.map((l) => ({ account_code: l.account_code, description: l.description, debit: Number(l.debit) || 0, credit: Number(l.credit) || 0 })),
        attachment: file,
      };
      onSaved(await api(initial ? `/journals/${initial.id}` : "/journals", { method: initial ? "PATCH" : "POST", body: JSON.stringify(payload) }));
    } catch (e) { setError(e.message); }
    setSaving(false);
  };
  const addAccount = (acc) => setAccounts((prev) => [...prev, acc].sort((a, b) => a.code.localeCompare(b.code)));
  return (
    <div className="workflow-page" data-testid="journal-form-page">
      <div className="page-heading">
        <div><p className="eyebrow">TRANSAKSI</p><h1>Jurnal Umum</h1></div>
      </div>
      {error && <div className="success-message error" data-testid="journal-form-error">{error}<button onClick={() => setError("")}><X size={14} /></button></div>}
      <div className="surface exp-form-wrap">
        <div className="exp-band ju-band" data-testid="journal-band">
          <label className="exp-field">No Transaksi<input data-testid="journal-number-input" value={initial ? initial.number : "[Auto]"} disabled /></label>
          <label className="exp-field">Tgl Transaksi<input data-testid="journal-date-input" type="date" value={head.date} onChange={(e) => setHead({ ...head, date: e.target.value })} /></label>
          <label className="exp-field ju-tag">Tag<input data-testid="journal-tag-input" value={head.tag} onChange={(e) => setHead({ ...head, tag: e.target.value })} /></label>
        </div>
        <table className="journal-line-table exp-line-table" data-testid="journal-line-table">
          <thead><tr><th className="ju-account-col">AKUN</th><th>DESKRIPSI</th><th className="right">DEBIT</th><th className="right">KREDIT</th><th /></tr></thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td>
                  <AccountSelect accounts={accounts} value={l.account_code} testid={`journal-line-account-${i}`}
                    onChange={(code) => setLine(i, "account_code", code)} onAccountCreated={addAccount} />
                </td>
                <td><input data-testid={`journal-line-description-${i}`} value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} /></td>
                <td><input data-testid={`journal-line-debit-${i}`} className="right" type="number" min="0" value={l.debit} onChange={(e) => setLine(i, "debit", e.target.value)} /></td>
                <td><input data-testid={`journal-line-credit-${i}`} className="right" type="number" min="0" value={l.credit} onChange={(e) => setLine(i, "credit", e.target.value)} /></td>
                <td>{lines.length > 2 && <button data-testid={`journal-line-remove-${i}`} className="icon-btn exp-remove" onClick={() => setLines(lines.filter((_, x) => x !== i))}><Minus size={14} /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <button data-testid="journal-add-line" className="primary-btn small exp-add-line" onClick={() => setLines([...lines, emptyLine()])}><Plus size={14} /> Tambah Data</button>
        <div className="exp-bottom">
          <div className="exp-bottom-left">
            <label>Memo<textarea data-testid="journal-memo-input" rows={4} value={head.memo} onChange={(e) => setHead({ ...head, memo: e.target.value })} /></label>
            <div className="exp-attachment">
              <span className="exp-attachment-label"><Paperclip size={13} /> Lampiran</span>
              <div className="exp-drop" data-testid="journal-attachment-drop" onClick={() => fileRef.current?.click()}
                onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}>
                {file ? <span className="exp-file-name"><Paperclip size={13} /> {file.filename} <button className="text-btn" onClick={(e) => { e.stopPropagation(); setFile(null); }}>Hapus</button></span> : <>Tarik file ke sini, atau <b>pilih file</b><small>ukuran maksimal 10 MB/file</small></>}
              </div>
              <input ref={fileRef} data-testid="journal-attachment-input" type="file" hidden onChange={(e) => pickFile(e.target.files?.[0])} />
            </div>
          </div>
          <div className="exp-bottom-right">
            <div className="ju-totals">
              <div><span>Total Debit</span><b data-testid="journal-total-debit">{money(totalDebit)}</b></div>
              <div><span>Total Kredit</span><b data-testid="journal-total-credit">{money(totalCredit)}</b></div>
            </div>
            {totalDebit > 0 && !balanced && <p className="ju-unbalanced" data-testid="journal-unbalanced-warning">Jurnal belum seimbang (selisih {money(Math.abs(totalDebit - totalCredit))})</p>}
          </div>
        </div>
        <div className="form-actions ju-actions">
          <button data-testid="journal-cancel-button" className="danger-btn" onClick={onCancel}><X size={14} /> Batal</button>
          <button data-testid="journal-save-button" className="ju-save-btn" disabled={!valid || saving} onClick={submit}>{saving ? "Menyimpan..." : initial ? "Simpan Perubahan" : "Buat Jurnal Umum"}</button>
        </div>
      </div>
    </div>
  );
}
